// Apresentação comercial da plataforma (pedido 2026-10-29: "tela de
// apresentação, rota /apresentacao, amanhã apresento o sistema para a
// Politracker — valor por placa R$ 2,00, as funcionalidades"). Página
// pública (bypass em __root.tsx isPublicStaticRoute), sem dados do backend:
// slides em tela cheia com rolagem por seção (setas/PageDown/espaço no
// projetor, deslizar no celular).
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Activity,
  Anchor,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CalendarCheck,
  Camera,
  Car,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Clock,
  Cpu,
  FileSignature,
  FileText,
  Globe,
  Hexagon,
  KeyRound,
  Layers,
  Lock,
  Map as MapIcon,
  MapPin,
  Maximize,
  MessageCircle,
  Navigation,
  Package,
  Palette,
  PersonStanding,
  Route as RouteIcon,
  Search,
  Share2,
  ShieldCheck,
  Siren,
  Smartphone,
  Truck,
  Upload,
  Users,
  Webhook,
  Wrench,
  Zap,
} from "lucide-react";
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";

export const Route = createFileRoute("/apresentacao")({
  head: () => ({
    meta: [
      { title: "Apresentação — Plataforma de Rastreamento" },
      { name: "description", content: "Plataforma de rastreamento veicular white label — mais segurança, mais controle, em tempo real." },
      { name: "theme-color", content: "#020617" },
    ],
  }),
  component: PresentationPage,
});

const CLIENT = "Politracker";
const CLIENT_DOMAIN = "politracker.gpsdrive.com.br";
const PRICE_PER_PLATE = 2;

const KEYFRAMES = `
@keyframes apFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
@keyframes apPulse { 0% { transform: scale(.6); opacity: .7 } 100% { transform: scale(2.6); opacity: 0 } }
@keyframes apDrive { 0% { offset-distance: 0% } 100% { offset-distance: 100% } }
@keyframes apGlow { 0%,100% { opacity: .35 } 50% { opacity: .7 } }
@keyframes apDash { to { stroke-dashoffset: -40 } }
.ap-reveal { opacity: 0; transform: translateY(28px); transition: opacity .7s ease, transform .7s ease; }
.ap-in .ap-reveal { opacity: 1; transform: none; }
.ap-in .ap-d1 { transition-delay: .08s } .ap-in .ap-d2 { transition-delay: .16s } .ap-in .ap-d3 { transition-delay: .24s }
.ap-in .ap-d4 { transition-delay: .32s } .ap-in .ap-d5 { transition-delay: .40s } .ap-in .ap-d6 { transition-delay: .48s }
`;

type Feature = { icon: typeof Car; title: string; text: string };

const SLIDES = [
  "capa",
  "marca",
  "mapa",
  "seguranca",
  "alertas",
  "relatorios",
  "app",
  "negocio",
  "operacao",
  "frota",
  "extras",
  "migracao",
  "investimento",
  "fim",
] as const;

function PresentationPage() {
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const goTo = useCallback((i: number) => {
    const el = scroller.current?.children[Math.max(0, Math.min(SLIDES.length - 1, i))] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Qual slide está na tela (pontos laterais + animação de entrada).
  useEffect(() => {
    const root = scroller.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("ap-in");
            if (e.intersectionRatio > 0.55) setActive(Number((e.target as HTMLElement).dataset.idx));
          }
        }
      },
      { root, threshold: [0.25, 0.6] },
    );
    Array.from(root.children).forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  // Projetor: setas, PageUp/PageDown, espaço, Home/End.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      if (["ArrowDown", "ArrowRight", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        goTo(active + 1);
      } else if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        goTo(active - 1);
      } else if (e.key === "Home") goTo(0);
      else if (e.key === "End") goTo(SLIDES.length - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, goTo]);

  const fullscreen = () => {
    try {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen();
    } catch {
      /* navegador sem tela cheia (iPhone) — segue normal */
    }
  };

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-slate-950 text-white">
      <style>{KEYFRAMES}</style>

      {/* Barra de progresso */}
      <div className="absolute inset-x-0 top-0 z-30 h-1 bg-white/5">
        <div
          className="h-full bg-gradient-to-r from-[#008CFF] to-emerald-400 transition-all duration-500"
          style={{ width: `${((active + 1) / SLIDES.length) * 100}%` }}
        />
      </div>

      {/* Navegação lateral */}
      <nav className="absolute right-3 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-2 md:flex">
        {SLIDES.map((s, i) => (
          <button
            key={s}
            type="button"
            aria-label={`Ir para o slide ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-2.5 rounded-full transition-all ${i === active ? "w-6 bg-[#008CFF]" : "w-2.5 bg-white/25 hover:bg-white/50"}`}
          />
        ))}
      </nav>

      <div className="absolute bottom-4 right-4 z-30 flex items-center gap-2">
        <span className="rounded-full bg-white/5 px-3 py-1.5 text-xs tabular-nums text-slate-400 ring-1 ring-white/10">
          {active + 1} / {SLIDES.length}
        </span>
        <button
          type="button"
          onClick={fullscreen}
          aria-label="Tela cheia"
          className="hidden h-9 w-9 place-items-center rounded-full bg-white/5 text-slate-300 ring-1 ring-white/10 hover:bg-white/10 md:grid"
        >
          <Maximize className="h-4 w-4" />
        </button>
      </div>

      <div ref={scroller} className="h-full snap-y snap-mandatory overflow-y-auto scroll-smooth">
        <Cover idx={0} onNext={() => goTo(1)} />
        <BrandSlide idx={1} />
        <MapSlide idx={2} />
        <FeatureSlide
          idx={3}
          kicker="Segurança"
          title="Controle total do veículo, de qualquer lugar"
          features={[
            { icon: Lock, title: "Bloqueio e desbloqueio remoto", text: "Pelo sistema ou pelo app, com confirmação do rastreador e envio por SMS como reserva." },
            { icon: Anchor, title: "Âncora", text: "Saiu do lugar, dispara o alerta na hora — ideal para a noite e o estacionamento." },
            { icon: Hexagon, title: "Cercas virtuais", text: "Círculo ou polígono com raio em metros ou km, todos os veículos de uma vez, bloqueio automático ao sair." },
            { icon: Share2, title: "Localização ao vivo por link", text: "Link com prazo para guincho, equipe de busca ou família, com rota até o veículo, satélite e Street View." },
            { icon: KeyRound, title: "Permissões por usuário", text: "Cada pessoa vê e faz só o que você liberar: bloquear, comandos, financeiro, relatórios." },
            { icon: ShieldCheck, title: "Acesso protegido", text: "Senha forte obrigatória para administradores, registro de quem fez cada ação." },
          ]}
        />
        <AlertsSlide idx={4} />
        <FeatureSlide
          idx={5}
          kicker="Histórico e relatórios"
          title="Tudo o que aconteceu, com prova"
          features={[
            { icon: RouteIcon, title: "Histórico de rotas", text: "Trajeto no mapa com reprodução, paradas, velocidade e mapa satélite." },
            { icon: FileText, title: "PDF profissional", text: "Relatório de rota em PDF com mapa do Google, pronto para enviar ao cliente." },
            { icon: BarChart3, title: "Relatórios completos", text: "Viagens, paradas, quilometragem, excesso de velocidade e eventos por período." },
            { icon: MapPin, title: "Endereços mais visitados", text: "Descubra onde o veículo passa mais tempo." },
            { icon: Zap, title: "Cortes de energia", text: "Cada corte de alimentação do rastreador registrado com hora e local." },
            { icon: Activity, title: "Painel da operação", text: "Veículos online, em movimento, parados e sem comunicação em tempo real." },
          ]}
        />
        <AppSlide idx={6} />
        <FeatureSlide
          idx={7}
          kicker="Gestão do seu negócio"
          title="Não é só rastrear: é administrar a empresa"
          features={[
            { icon: Users, title: "Clientes e usuários", text: "Cadastro completo, sub-usuários, importação em massa e envio do login por WhatsApp." },
            { icon: CircleDollarSign, title: "Cobrança automática", text: "Boleto e PIX recorrentes integrados ao Asaas, com aviso de vencimento." },
            { icon: FileSignature, title: "Contrato digital", text: "Assinatura pelo celular com foto ao vivo e verificação de rosto por inteligência artificial." },
            { icon: BarChart3, title: "Financeiro", text: "Contas a pagar e receber, parceladas e fixas, num menu próprio." },
            { icon: Building2, title: "Revendas", text: "Crie revendas abaixo da sua empresa, cada uma com seus clientes." },
            { icon: ClipboardCheck, title: "Vendas e pré-contratos", text: "Do primeiro contato ao contrato assinado, sem papel." },
          ]}
        />
        <FeatureSlide
          idx={8}
          kicker="Operação técnica"
          title="Agenda de instalação na palma da mão"
          features={[
            { icon: CalendarCheck, title: "Agenda de atendimentos", text: "Instalações, manutenções e retiradas por técnico, com calendário e histórico de quem alterou." },
            { icon: Smartphone, title: "App do técnico", text: "O técnico recebe o serviço, testa o equipamento e conclui pelo celular." },
            { icon: Package, title: "Estoque", text: "Rastreadores e acessórios por local e técnico, com entrada e saída." },
            { icon: Cpu, title: "Gestão de chips", text: "Linhas, operadoras e vínculo com cada rastreador." },
            { icon: Wrench, title: "Teste de equipamento", text: "Confirma posição, ignição e bloqueio antes de liberar o veículo." },
            { icon: ClipboardCheck, title: "Chamados", text: "Pedidos do cliente organizados até a solução." },
          ]}
        />
        <FeatureSlide
          idx={9}
          kicker="Gestão de frota"
          title="Para clientes com frota e motoristas"
          features={[
            { icon: Truck, title: "Painel da frota", text: "Indicadores do dia por veículo e por motorista." },
            { icon: Users, title: "Motoristas", text: "Cadastro, documentos, contratos e cobrança recorrente do motorista." },
            { icon: ClipboardCheck, title: "Vistorias por link", text: "O motorista faz a vistoria com fotos pelo celular, sem precisar de conta." },
            { icon: Wrench, title: "Manutenção", text: "Revisões e trocas programadas por data ou quilometragem." },
            { icon: BarChart3, title: "Relatórios da frota", text: "Rotas, uso e custos por veículo com mapa satélite." },
            { icon: Smartphone, title: "Tudo no app", text: "Menu de Gestão de Frota completo também no celular." },
          ]}
        />
        <FeatureSlide
          idx={10}
          kicker="Diferenciais"
          title="Recursos que fazem a diferença na venda"
          features={[
            { icon: Search, title: "Consulta de antecedentes", text: "CPF com processos, mandados, protestos e dívidas — antes de entregar o veículo." },
            { icon: Siren, title: "Assistência 24h", text: "Acionamento de guincho com acompanhamento ao vivo pelo cliente." },
            { icon: Camera, title: "Câmeras veiculares", text: "Vídeo ao vivo, alarmes e áudio para equipamentos com câmera." },
            { icon: Bot, title: "Assistente com IA", text: "Responde dúvidas e ajuda na operação do dia a dia." },
            { icon: MessageCircle, title: "WhatsApp integrado", text: "Alertas, faturas e logins enviados pelo WhatsApp da sua empresa." },
            { icon: Webhook, title: "API e Webhook", text: "Integre a plataforma com o seu sistema." },
          ]}
        />
        <MigrationSlide idx={11} />
        <PricingSlide idx={12} />
        <EndSlide idx={13} onTop={() => goTo(0)} />
      </div>
    </div>
  );
}

// --- Base -------------------------------------------------------------------

function Slide({ idx, children, className = "" }: { idx: number; children: ReactNode; className?: string }) {
  return (
    <section
      data-idx={idx}
      className={`relative flex min-h-[100dvh] w-full snap-start items-center justify-center overflow-hidden px-5 py-16 md:px-12 ${className}`}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-[#008CFF]/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-emerald-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>
      <div className="relative w-full max-w-6xl">{children}</div>
    </section>
  );
}

function Kicker({ children }: { children: ReactNode }) {
  return (
    <div className="ap-reveal mb-3 inline-flex items-center gap-2 rounded-full bg-[#008CFF]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#4db1ff] ring-1 ring-[#008CFF]/30">
      {children}
    </div>
  );
}

function Title({ children }: { children: ReactNode }) {
  return <h2 className="ap-reveal ap-d1 text-3xl font-black leading-tight tracking-tight md:text-5xl">{children}</h2>;
}

function FeatureCard({ f, i }: { f: Feature; i: number }) {
  const Icon = f.icon;
  return (
    <div
      className={`ap-reveal ap-d${Math.min(6, i + 1)} group rounded-2xl bg-white/[0.04] p-4 ring-1 ring-white/10 transition hover:bg-white/[0.07] hover:ring-[#008CFF]/40 md:p-5`}
    >
      <div className="mb-3 grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-[#008CFF] to-[#0060c0] shadow-lg shadow-[#008CFF]/20">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[15px] font-bold md:text-base">{f.title}</div>
      <div className="mt-1 text-sm leading-relaxed text-slate-400">{f.text}</div>
    </div>
  );
}

function FeatureSlide({ idx, kicker, title, features }: { idx: number; kicker: string; title: string; features: Feature[] }) {
  return (
    <Slide idx={idx}>
      <Kicker>{kicker}</Kicker>
      <Title>{title}</Title>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 md:gap-4">
        {features.map((f, i) => (
          <FeatureCard key={f.title} f={f} i={i} />
        ))}
      </div>
    </Slide>
  );
}

// --- Slides especiais ---------------------------------------------------------

function Cover({ idx, onNext }: { idx: number; onNext: () => void }) {
  return (
    <Slide idx={idx}>
      <div className="flex flex-col items-center text-center">
        <img src={defaultLogoUrl} alt="GPS Drive" className="ap-reveal h-16 w-auto md:h-24" style={{ animation: "apFloat 5s ease-in-out infinite" }} />
        <div className="ap-reveal ap-d1 mt-8 text-xs font-bold uppercase tracking-[0.3em] text-emerald-400 md:text-sm">
          Proposta exclusiva para {CLIENT}
        </div>
        <h1 className="ap-reveal ap-d2 mt-4 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight md:text-7xl">
          A plataforma completa de{" "}
          <span className="bg-gradient-to-r from-[#008CFF] to-emerald-400 bg-clip-text text-transparent">rastreamento</span> com a sua marca
        </h1>
        <p className="ap-reveal ap-d3 mt-6 max-w-2xl text-base text-slate-400 md:text-xl">
          Mapa ao vivo, bloqueio, alertas, app próprio, cobrança, contratos, agenda de técnicos e gestão de frota — num só sistema.
        </p>
        <div className="ap-reveal ap-d4 mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-slate-300">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#008CFF]" /> Mais segurança</span>
          <span className="flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-[#008CFF]" /> Mais controle</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#008CFF]" /> Em tempo real</span>
        </div>
        <button
          type="button"
          onClick={onNext}
          className="ap-reveal ap-d5 mt-12 flex flex-col items-center gap-1 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-white"
        >
          Começar
          <ChevronDown className="h-6 w-6" style={{ animation: "apFloat 2s ease-in-out infinite" }} />
        </button>
      </div>
    </Slide>
  );
}

function BrandSlide({ idx }: { idx: number }) {
  return (
    <Slide idx={idx}>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <Kicker>White label</Kicker>
          <Title>
            A {CLIENT} já está <span className="text-emerald-400">pronta</span> na plataforma
          </Title>
          <p className="ap-reveal ap-d2 mt-5 text-base text-slate-400 md:text-lg">
            Seus clientes enxergam só a sua marca: endereço próprio, logo, cores e tela de login personalizadas.
          </p>
          <div className="mt-6 space-y-3">
            {[
              { icon: Globe, t: "Endereço próprio", s: CLIENT_DOMAIN },
              { icon: Palette, t: "Logo e cores da sua empresa", s: "No sistema, no app e nos links enviados" },
              { icon: Smartphone, t: "App para os seus clientes", s: "Android, iPhone e instalável pelo navegador" },
            ].map((r, i) => (
              <div key={r.t} className={`ap-reveal ap-d${i + 3} flex items-center gap-3 rounded-xl bg-white/[0.04] p-3 ring-1 ring-white/10`}>
                <r.icon className="h-5 w-5 shrink-0 text-[#4db1ff]" />
                <div className="min-w-0">
                  <div className="text-sm font-bold">{r.t}</div>
                  <div className="truncate text-xs text-slate-400">{r.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Navegador ilustrativo */}
        <div className="ap-reveal ap-d3">
          <div className="overflow-hidden rounded-2xl bg-slate-900 shadow-2xl shadow-[#008CFF]/10 ring-1 ring-white/10">
            <div className="flex items-center gap-2 border-b border-white/10 bg-slate-800/80 px-3 py-2.5">
              <span className="h-3 w-3 rounded-full bg-red-400/80" />
              <span className="h-3 w-3 rounded-full bg-amber-400/80" />
              <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
              <div className="ml-2 flex flex-1 items-center gap-1.5 truncate rounded-md bg-slate-950/70 px-3 py-1 text-xs text-slate-300">
                <Lock className="h-3 w-3 text-emerald-400" /> https://{CLIENT_DOMAIN}
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 bg-gradient-to-b from-slate-900 to-slate-950 px-8 py-12">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-emerald-400 to-[#008CFF] text-2xl font-black">
                {CLIENT.slice(0, 1)}
              </div>
              <div className="text-xl font-black tracking-tight">{CLIENT}</div>
              <div className="w-full max-w-xs space-y-2.5">
                <div className="h-10 rounded-lg bg-white/5 ring-1 ring-white/10" />
                <div className="h-10 rounded-lg bg-white/5 ring-1 ring-white/10" />
                <div className="grid h-10 place-items-center rounded-lg bg-[#008CFF] text-sm font-bold">Entrar</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function MapSlide({ idx }: { idx: number }) {
  return (
    <Slide idx={idx}>
      <div className="grid items-center gap-10 md:grid-cols-[1.1fr_1fr]">
        <div className="ap-reveal order-2 md:order-1">
          <FakeMap />
        </div>
        <div className="order-1 md:order-2">
          <Kicker>Tempo real</Kicker>
          <Title>Toda a frota ao vivo, num só mapa</Title>
          <div className="mt-6 space-y-3">
            {[
              { icon: Navigation, t: "Movimento suave", s: "O veículo desliza pela rua em tempo real, com rastro do trajeto." },
              { icon: Layers, t: "Mapa, satélite e trânsito", s: "Google Maps com camadas e alertas do Waze." },
              { icon: PersonStanding, t: "Street View", s: "Veja a rua onde o veículo está, sem sair do sistema." },
              { icon: Car, t: "Ícones realistas", s: "Carro, moto, caminhão e utilitário na cor do veículo, com foto." },
              { icon: MapIcon, t: "Milhares de veículos", s: "Agrupamento automático no mapa, rápido mesmo com frota grande." },
            ].map((r, i) => (
              <div key={r.t} className={`ap-reveal ap-d${i + 1} flex gap-3`}>
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#008CFF]/15 text-[#4db1ff]">
                  <r.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-bold md:text-base">{r.t}</div>
                  <div className="text-sm text-slate-400">{r.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}

// Mapa ilustrativo em SVG (sem chave de mapa nem dado real).
function FakeMap() {
  const path = "M40,300 C120,260 140,180 220,170 S340,120 380,60";
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-[#0b1324] shadow-2xl ring-1 ring-white/10">
      <svg viewBox="0 0 420 340" className="absolute inset-0 h-full w-full">
        <g stroke="#1e293b" strokeWidth="14" fill="none" strokeLinecap="round">
          <path d="M0,90 L420,130" />
          <path d="M0,250 L420,210" />
          <path d="M110,0 L150,340" />
          <path d="M300,0 L270,340" />
        </g>
        <g stroke="#334155" strokeWidth="1.5" fill="none" strokeDasharray="8 8">
          <path d="M0,90 L420,130" />
          <path d="M0,250 L420,210" />
        </g>
        <circle cx="300" cy="230" r="46" fill="#10b98122" stroke="#10b981" strokeWidth="2" strokeDasharray="6 5" />
        <path d={path} stroke="#008CFF" strokeWidth="5" fill="none" strokeLinecap="round" opacity=".9" />
        <path d={path} stroke="#fff" strokeWidth="2" fill="none" strokeDasharray="10 10" style={{ animation: "apDash 1.2s linear infinite" }} opacity=".7" />
      </svg>
      <div
        className="absolute left-0 top-0"
        style={{ offsetPath: `path("${path}")`, offsetRotate: "auto", animation: "apDrive 7s ease-in-out infinite alternate" } as React.CSSProperties}
      >
        <div className="relative -translate-x-1/2 -translate-y-1/2">
          <span className="absolute inset-0 rounded-full bg-[#008CFF]" style={{ animation: "apPulse 1.8s ease-out infinite" }} />
          <div className="relative grid h-7 w-7 place-items-center rounded-full bg-[#008CFF] ring-2 ring-white">
            <Navigation className="h-3.5 w-3.5 rotate-90 fill-white" />
          </div>
        </div>
      </div>
      {[
        { l: "18%", t: "22%", c: "bg-emerald-500" },
        { l: "70%", t: "68%", c: "bg-slate-400" },
        { l: "84%", t: "30%", c: "bg-emerald-500" },
        { l: "40%", t: "80%", c: "bg-red-500" },
      ].map((m, i) => (
        <div key={i} className={`absolute h-4 w-4 rounded-full ring-2 ring-white ${m.c}`} style={{ left: m.l, top: m.t }} />
      ))}
      <div className="absolute left-3 top-3 rounded-xl bg-slate-950/90 px-3 py-2 text-xs ring-1 ring-white/10 backdrop-blur">
        <div className="font-bold">ABC-1D23 · Em movimento</div>
        <div className="text-slate-400">62 km/h · Av. Paulista, 1000</div>
      </div>
      <div className="absolute bottom-3 left-3 flex gap-1.5 text-[11px] font-semibold">
        <span className="rounded-md bg-emerald-500/20 px-2 py-1 text-emerald-300">● 128 online</span>
        <span className="rounded-md bg-red-500/20 px-2 py-1 text-red-300">● 3 bloqueados</span>
      </div>
      <div className="absolute bottom-3 right-3 text-[10px] text-slate-500">Ilustração</div>
    </div>
  );
}

function AlertsSlide({ idx }: { idx: number }) {
  const alerts = [
    { icon: Zap, t: "Ignição ligada / desligada", c: "text-amber-300" },
    { icon: Activity, t: "Excesso de velocidade", c: "text-red-300" },
    { icon: Hexagon, t: "Entrada e saída de cerca", c: "text-emerald-300" },
    { icon: Siren, t: "Bateria violada / corte de energia", c: "text-red-300" },
    { icon: Anchor, t: "Âncora: veículo saiu do lugar", c: "text-sky-300" },
    { icon: Clock, t: "Falha de comunicação", c: "text-slate-300" },
  ];
  return (
    <Slide idx={idx}>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <Kicker>Alertas inteligentes</Kicker>
          <Title>O cliente fica sabendo antes de virar problema</Title>
          <p className="ap-reveal ap-d2 mt-5 text-base text-slate-400 md:text-lg">
            Notificação no celular com som, aviso pelo WhatsApp e registro no histórico. Um alerta por ocorrência, sem enxurrada de mensagens.
          </p>
          <div className="ap-reveal ap-d3 mt-6 flex flex-wrap gap-2 text-xs font-semibold">
            {["Push no app", "WhatsApp", "Som personalizado", "Bloqueio automático"].map((t) => (
              <span key={t} className="rounded-full bg-white/5 px-3 py-1.5 ring-1 ring-white/10">{t}</span>
            ))}
          </div>
        </div>
        <div className="space-y-2.5">
          {alerts.map((a, i) => (
            <div
              key={a.t}
              className={`ap-reveal ap-d${i + 1} flex items-center gap-3 rounded-2xl bg-slate-900/80 p-3.5 shadow-lg ring-1 ring-white/10 backdrop-blur`}
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/5">
                <a.icon className={`h-5 w-5 ${a.c}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">{a.t}</div>
                <div className="text-xs text-slate-500">agora · ABC-1D23</div>
              </div>
              <Bell className="h-4 w-4 text-slate-500" />
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function AppSlide({ idx }: { idx: number }) {
  return (
    <Slide idx={idx}>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div className="ap-reveal flex justify-center">
          {/* Celular ilustrativo */}
          <div className="relative h-[460px] w-[230px] rounded-[2.5rem] bg-slate-900 p-2.5 shadow-2xl shadow-[#008CFF]/20 ring-1 ring-white/15" style={{ animation: "apFloat 6s ease-in-out infinite" }}>
            <div className="relative h-full w-full overflow-hidden rounded-[2rem] bg-gradient-to-b from-[#0b1a33] to-slate-950">
              <div className="absolute left-1/2 top-2 h-5 w-20 -translate-x-1/2 rounded-full bg-black" />
              <div className="px-4 pt-10">
                <div className="text-[11px] text-slate-400">Bom dia</div>
                <div className="text-base font-black">Meus veículos</div>
                <div className="mt-3 rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">ABC-1D23</span>
                    <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">LIGADO</span>
                  </div>
                  <div className="mt-1 text-[10px] text-slate-400">Av. Paulista, 1000 · 62 km/h</div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="grid h-9 place-items-center rounded-lg bg-red-600 text-[11px] font-bold">Bloquear</div>
                    <div className="grid h-9 place-items-center rounded-lg bg-white/10 text-[11px] font-bold">Rota</div>
                  </div>
                </div>
                <div className="mt-3 h-36 overflow-hidden rounded-2xl ring-1 ring-white/10">
                  <FakeMiniMap />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 flex justify-around border-t border-white/10 bg-slate-950/90 py-2.5 text-[9px] text-slate-400">
                {["Início", "Mapa", "Frota", "Perfil"].map((t, i) => (
                  <span key={t} className={i === 1 ? "font-bold text-[#4db1ff]" : ""}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div>
          <Kicker>App do cliente</Kicker>
          <Title>O cliente no controle, pelo celular</Title>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              { icon: Smartphone, t: "Android e iPhone", s: "Publicado nas lojas, com a sua marca" },
              { icon: Globe, t: "Sem instalar", s: "Também funciona direto no navegador" },
              { icon: Lock, t: "Bloqueio com 1 toque", s: "Com confirmação do rastreador" },
              { icon: RouteIcon, t: "Histórico e PDF", s: "Rotas do dia direto no celular" },
              { icon: Share2, t: "Compartilhar ao vivo", s: "Link com prazo para quem precisar" },
              { icon: FileText, t: "Faturas e PIX", s: "Segunda via e pagamento no app" },
            ].map((r, i) => (
              <div key={r.t} className={`ap-reveal ap-d${i + 1} rounded-xl bg-white/[0.04] p-3 ring-1 ring-white/10`}>
                <r.icon className="mb-2 h-5 w-5 text-[#4db1ff]" />
                <div className="text-sm font-bold">{r.t}</div>
                <div className="text-xs text-slate-400">{r.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}

function FakeMiniMap() {
  return (
    <svg viewBox="0 0 200 140" className="h-full w-full bg-[#0b1324]">
      <g stroke="#1e293b" strokeWidth="8" fill="none">
        <path d="M0,40 L200,60" />
        <path d="M60,0 L80,140" />
        <path d="M0,110 L200,95" />
      </g>
      <path d="M20,120 C60,100 70,60 120,55 S170,30 185,20" stroke="#008CFF" strokeWidth="3" fill="none" />
      <circle cx="185" cy="20" r="6" fill="#008CFF" stroke="#fff" strokeWidth="2" />
    </svg>
  );
}

function MigrationSlide({ idx }: { idx: number }) {
  const steps = [
    { icon: Upload, t: "Importação em massa", s: "Clientes, usuários e rastreadores por planilha." },
    { icon: Car, t: "Vínculo automático", s: "Placas e IMEIs ligados aos clientes de uma vez." },
    { icon: Cpu, t: "Vários protocolos", s: "Compatível com os principais rastreadores do mercado." },
    { icon: ShieldCheck, t: "Sem perder histórico", s: "Seus clientes continuam rastreando durante a troca." },
  ];
  return (
    <Slide idx={idx}>
      <Kicker>Migração</Kicker>
      <Title>Trocar de plataforma sem dor de cabeça</Title>
      <div className="mt-10 grid gap-4 md:grid-cols-4">
        {steps.map((s, i) => (
          <div key={s.t} className={`ap-reveal ap-d${i + 1} relative rounded-2xl bg-white/[0.04] p-5 ring-1 ring-white/10`}>
            <div className="absolute -top-3 left-5 grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-xs font-black text-slate-950">
              {i + 1}
            </div>
            <s.icon className="mb-3 mt-2 h-6 w-6 text-[#4db1ff]" />
            <div className="font-bold">{s.t}</div>
            <div className="mt-1 text-sm text-slate-400">{s.s}</div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function PricingSlide({ idx }: { idx: number }) {
  const [plates, setPlates] = useState(500);
  const total = plates * PRICE_PER_PLATE;
  return (
    <Slide idx={idx}>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <Kicker>Investimento</Kicker>
          <Title>Simples e justo: você paga por placa</Title>
          <p className="ap-reveal ap-d2 mt-5 text-base text-slate-400 md:text-lg">
            Toda a plataforma liberada para a {CLIENT}. O valor acompanha o tamanho da sua base.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm md:text-base">
            {[
              "Sistema web com a sua marca",
              "App do cliente Android, iPhone e navegador",
              "Alertas, bloqueio, relatórios e histórico",
              "Gestão de clientes, cobrança e contratos",
              "Agenda de técnicos, estoque e gestão de frota",
            ].map((t, i) => (
              <li key={t} className={`ap-reveal ap-d${i + 2} flex items-center gap-2.5`}>
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-500/20 text-emerald-400"><Check className="h-3 w-3" /></span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="ap-reveal ap-d2">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a2a55] via-slate-900 to-slate-950 p-6 shadow-2xl shadow-[#008CFF]/20 ring-1 ring-[#008CFF]/40 md:p-8">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#008CFF]/30 blur-3xl" style={{ animation: "apGlow 4s ease-in-out infinite" }} />
            <div className="relative">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4db1ff]">Valor por placa</div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-6xl font-black tracking-tight md:text-7xl">{brl(PRICE_PER_PLATE)}</span>
                <span className="mb-3 text-slate-400">/ placa por mês</span>
              </div>

              <div className="mt-8 rounded-2xl bg-slate-950/60 p-4 ring-1 ring-white/10">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-300">Simule sua base</span>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      max={100000}
                      value={plates}
                      onChange={(e) => setPlates(Math.max(0, Math.min(100000, Math.round(Number(e.target.value) || 0))))}
                      className="w-24 rounded-lg bg-white/10 px-2 py-1 text-right font-bold tabular-nums text-white outline-none ring-1 ring-white/15 focus:ring-[#008CFF]"
                    />
                    <span className="text-slate-400">placas</span>
                  </label>
                </div>
                <input
                  type="range"
                  min={10}
                  max={10000}
                  step={10}
                  value={Math.min(10000, Math.max(10, plates))}
                  onChange={(e) => setPlates(Number(e.target.value))}
                  className="mt-4 w-full accent-[#008CFF]"
                />
                <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
                  <span className="text-sm text-slate-400">Total mensal</span>
                  <span className="text-3xl font-black tabular-nums text-emerald-400">{brl(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function EndSlide({ idx, onTop }: { idx: number; onTop: () => void }) {
  return (
    <Slide idx={idx}>
      <div className="flex flex-col items-center text-center">
        <img src={defaultLogoUrl} alt="GPS Drive" className="ap-reveal h-14 w-auto md:h-20" />
        <h2 className="ap-reveal ap-d1 mt-8 max-w-3xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
          Vamos colocar a <span className="bg-gradient-to-r from-[#008CFF] to-emerald-400 bg-clip-text text-transparent">{CLIENT}</span> em
          movimento?
        </h2>
        <p className="ap-reveal ap-d2 mt-5 max-w-xl text-base text-slate-400 md:text-lg">
          Seu ambiente já está criado. Basta importar os clientes e começar a rastrear.
        </p>
        <a
          href={`https://${CLIENT_DOMAIN}`}
          target="_blank"
          rel="noreferrer"
          className="ap-reveal ap-d3 mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#008CFF] px-6 py-4 text-base font-bold shadow-xl shadow-[#008CFF]/30 transition hover:bg-[#0077dd]"
        >
          <Globe className="h-5 w-5" /> {CLIENT_DOMAIN}
        </a>
        <div className="ap-reveal ap-d4 mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-slate-300">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#008CFF]" /> Mais segurança</span>
          <span className="flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-[#008CFF]" /> Mais controle</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#008CFF]" /> Em tempo real</span>
        </div>
        <button type="button" onClick={onTop} className="ap-reveal ap-d5 mt-10 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-white">
          Voltar ao início
        </button>
      </div>
    </Slide>
  );
}
